import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  BedDouble, 
  Calendar, 
  Users, 
  LogIn, 
  CreditCard, 
  UtensilsCrossed,
  ClipboardList,
  UserCog,
  LogOut,
  Building2,
  ChevronDown,
  CalendarDays,
  FileText,
  BarChart3,
  Globe,
  Bed,
  Sparkles,
  Shield,
  Moon,
  Ticket,
  TrendingUp,
  MonitorSmartphone
} from 'lucide-react';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/rooms', icon: BedDouble, label: 'Rooms' },
  { path: '/beds', icon: Bed, label: 'Dorm Beds' },
  { path: '/room-calendar', icon: CalendarDays, label: 'Room Calendar' },
  { path: '/bookings', icon: Calendar, label: 'Bookings' },
  { path: '/direct-bookings', icon: MonitorSmartphone, label: 'Direct Bookings' },
  { path: '/guests', icon: Users, label: 'Guests' },
  { path: '/check-in-out', icon: LogIn, label: 'Check In/Out' },
  { path: '/housekeeping', icon: Sparkles, label: 'Housekeeping' },
  { path: '/payments', icon: CreditCard, label: 'Payments' },
  { path: '/invoices', icon: FileText, label: 'Invoices' },
  { path: '/ota-integration', icon: Globe, label: 'OTA Channels' },
  { path: '/menu', icon: UtensilsCrossed, label: 'F&B Menu' },
  { path: '/orders', icon: ClipboardList, label: 'Orders' },
  { path: '/coupons', icon: Ticket, label: 'Coupons' },
  { path: '/dynamic-pricing', icon: TrendingUp, label: 'Pricing' },
  { path: '/documents', icon: Shield, label: 'Compliance' },
  { path: '/night-audit', icon: Moon, label: 'Night Audit' },
  { path: '/reports', icon: BarChart3, label: 'Reports' },
];

const adminItems = [
  { path: '/staff', icon: UserCog, label: 'Staff Management' },
];

export const Sidebar = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="sidebar fixed left-0 top-0 h-screen w-60 flex flex-col z-50" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-stone-800">
        <div className="flex items-center gap-2.5">
          <Building2 className="w-7 h-7 text-sage-500" />
          <div>
            <span className="font-bold text-lg tracking-tight text-white font-['Manrope']">Nomi-Connect</span>
            <p className="text-xs text-stone-500">by GoNomikai</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-4.5 h-4.5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {isAdmin && (
          <div className="mt-6 pt-4 border-t border-stone-800">
            <p className="px-3 mb-2 text-xs font-semibold text-stone-500 uppercase tracking-wider">Admin</p>
            <div className="space-y-1">
              {adminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4.5 h-4.5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-stone-800">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-between text-stone-300 hover:text-white hover:bg-stone-800"
              data-testid="user-menu-trigger"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-sage-700 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white">
            <DropdownMenuItem className="text-stone-600">
              {user?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-red-600 focus:text-red-600 cursor-pointer"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Sidebar />
      <main className="ml-60 min-h-screen">
        <header className="sticky top-0 z-30 h-14 px-6 lg:px-8 flex items-center justify-end bg-stone-50/80 backdrop-blur-md border-b border-stone-200/70" data-testid="app-topbar">
          <NotificationBell />
        </header>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
